import createRequest from '../../../shared/api/portalApiClient';
import { env } from '../../../config/env';
import { resolveApiBaseForPath } from '../../../shared/api/requestRouting';
import { logger } from '../../../shared/utils/logger.js';

/**
 * API client for the Services + Employees + Roles module.
 *
 * Phase C4 introduces only the Role surface. Employee + schedule + assignment
 * + availability clients will arrive in C5–C7.
 */

export type EmployeeRoleRow = {
  roleUuid: string;
  name: string;
  description: string;
  status: 'ACTIVE' | 'INACTIVE' | string;
  createdAt: string;
  updatedAt: string;
};

export type EmployeeRoleWriteInput = {
  name: string;
  description?: string;
};

export type EmployeeRoleStatusInput = {
  status: 'ACTIVE' | 'INACTIVE';
};

export type EmployeeRoleSummary = {
  roleUuid: string;
  name: string;
  status: string;
};

export type EmployeeRow = {
  employeeUuid: string;
  name: string;
  phone: string;
  email: string;
  status: 'ACTIVE' | 'INACTIVE' | 'ON_LEAVE' | 'TERMINATED' | string;
  showInApp: boolean;
  gender: 'MALE' | 'FEMALE' | 'UNSPECIFIED' | string;
  role: EmployeeRoleSummary | null;
  createdAt: string;
  updatedAt: string;
};

export type EmployeeWriteInput = {
  name: string;
  phone?: string;
  email?: string;
  roleUuid: string;
  showInApp?: boolean;
  gender?: 'MALE' | 'FEMALE' | 'UNSPECIFIED' | string;
};

export type EmployeeStatusInput = {
  status: 'ACTIVE' | 'INACTIVE' | 'ON_LEAVE' | 'TERMINATED';
};

export type EmployeeDocumentType =
  | 'AADHAAR'
  | 'DRIVING_LICENSE'
  | 'OFFICIAL_ID_OTHER';

export type EmployeeDocumentRow = {
  documentUuid: string;
  employeeUuid: string;
  documentType: EmployeeDocumentType | string;
  displayName: string;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  documentUrl: string;
  createdAt: string;
};

export type EmployeeDocumentUploadInput = {
  documentType: EmployeeDocumentType;
  file: File;
  displayName?: string;
};

export type EmployeeDocumentAccessOtpIssue = {
  maskedEmail: string;
  otpTtlMinutes: number;
};

export type EmployeeDocumentAccessGrant = {
  accessToken: string;
  expiresInMinutes: number;
};

export type EmployeeListFilters = {
  status?: string;
  roleUuid?: string;
};

export type TimeWindow = {
  start: string; // HH:mm
  end: string;   // HH:mm
};

export type WeeklySchedule = Record<string, TimeWindow[]>;

export const SCHEDULE_DAYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
] as const;

export type ScheduleDay = (typeof SCHEDULE_DAYS)[number];

export type ScheduleExceptionType = 'DAY_OFF' | 'CUSTOM_HOURS';

export type ScheduleException = {
  date: string; // ISO date
  type: ScheduleExceptionType;
  intervals: TimeWindow[];
};

export type EmployeeScheduleDetail = {
  scheduleUuid: string;
  employeeUuid: string;
  weeklySchedule: WeeklySchedule;
  exceptions: ScheduleException[];
  effectiveFrom: string | null;
  updatedAt: string | null;
};

export type EmployeeScheduleWriteInput = {
  weeklySchedule: WeeklySchedule;
  effectiveFrom?: string | null;
};

export type EmployeeScheduleExceptionWriteInput = {
  date: string;
  type: ScheduleExceptionType;
  intervals?: TimeWindow[];
};

export type AssignmentRow = {
  assignmentUuid: string;
  employeeUuid: string;
  serviceUuid: string;
  priceOverride: string | null;
  isPrimary: boolean;
  visibility: 'PUBLIC' | 'HIDDEN' | string;
  createdAt: string;
  updatedAt: string;
};

export type AssignmentWriteInput = {
  priceOverride?: string | null;
  isPrimary?: boolean;
  visibility?: 'PUBLIC' | 'HIDDEN';
};

/** Minimal service shape needed by the assignment-manager picker. */
export type ServicePickerRow = {
  serviceUuid: string;
  serviceName: string;
  status: string;
};

type ApiEnvelope<T> = { data?: T };

const request = createRequest(env.apiBase || '');

const unwrap = <T>(payload: ApiEnvelope<T> | T): T => {
  if (payload && typeof payload === 'object' && 'data' in (payload as object)) {
    return (payload as ApiEnvelope<T>).data as T;
  }
  return payload as T;
};

const asText = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

const mapRole = (raw: unknown): EmployeeRoleRow => {
  const r = (raw || {}) as Record<string, unknown>;
  return {
    roleUuid: asText(r.roleUuid),
    name: asText(r.name),
    description: asText(r.description),
    status: asText(r.status),
    createdAt: asText(r.createdAt),
    updatedAt: asText(r.updatedAt),
  };
};

const ROLES_PATH = '/v1/profile/stores/staffing/roles';
const EMPLOYEES_PATH = '/v1/profile/stores/staffing/employees';
const STAFFING_SERVICES_PATH = '/v1/profile/stores/staffing/services';
const CATALOG_SERVICES_BY_CATEGORY_PATH =
  '/v1/profile/stores/appointments/catalog/services/by-category';
const schedulePath = (employeeUuid: string): string =>
  `${EMPLOYEES_PATH}/${encodeURIComponent(employeeUuid)}/schedule`;
const employeeAssignmentsPath = (employeeUuid: string): string =>
  `${EMPLOYEES_PATH}/${encodeURIComponent(employeeUuid)}/services`;
const serviceAssignmentsPath = (serviceUuid: string): string =>
  `${STAFFING_SERVICES_PATH}/${encodeURIComponent(serviceUuid)}/employees`;
const employeeDocumentsPath = (employeeUuid: string): string =>
  `${EMPLOYEES_PATH}/${encodeURIComponent(employeeUuid)}/documents`;
const employeeDocumentAccessPath = (employeeUuid: string, documentUuid: string): string =>
  `${employeeDocumentsPath(employeeUuid)}/${encodeURIComponent(documentUuid)}/access`;

const mapAssignment = (raw: unknown): AssignmentRow => {
  const r = (raw || {}) as Record<string, unknown>;
  return {
    assignmentUuid: asText(r.assignmentUuid),
    employeeUuid: asText(r.employeeUuid),
    serviceUuid: asText(r.serviceUuid),
    priceOverride: r.priceOverride !== null && r.priceOverride !== undefined
      ? asText(r.priceOverride)
      : null,
    isPrimary: Boolean(r.isPrimary),
    visibility: asText(r.visibility) || 'PUBLIC',
    createdAt: asText(r.createdAt),
    updatedAt: asText(r.updatedAt),
  };
};

const mapServicePickerRow = (raw: unknown): ServicePickerRow => {
  const r = (raw || {}) as Record<string, unknown>;
  return {
    serviceUuid: asText(r.serviceUuid),
    serviceName: asText(r.serviceName),
    status: asText(r.status) || 'ACTIVE',
  };
};

const mapTimeWindow = (raw: unknown): TimeWindow => {
  const r = (raw || {}) as Record<string, unknown>;
  return { start: asText(r.start), end: asText(r.end) };
};

const mapWeeklySchedule = (raw: unknown): WeeklySchedule => {
  if (!raw || typeof raw !== 'object') return {};
  const result: WeeklySchedule = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!Array.isArray(value)) continue;
    result[key.toUpperCase()] = value.map(mapTimeWindow);
  }
  return result;
};

const mapScheduleException = (raw: unknown): ScheduleException => {
  const r = (raw || {}) as Record<string, unknown>;
  const type = asText(r.type).toUpperCase() as ScheduleExceptionType;
  const intervals = Array.isArray(r.intervals)
    ? (r.intervals as unknown[]).map(mapTimeWindow)
    : [];
  return { date: asText(r.date), type, intervals };
};

const mapScheduleDetail = (raw: unknown): EmployeeScheduleDetail => {
  const r = (raw || {}) as Record<string, unknown>;
  const exceptions = Array.isArray(r.exceptions)
    ? (r.exceptions as unknown[]).map(mapScheduleException)
    : [];
  return {
    scheduleUuid: asText(r.scheduleUuid),
    employeeUuid: asText(r.employeeUuid),
    weeklySchedule: mapWeeklySchedule(r.weeklySchedule),
    exceptions,
    effectiveFrom: r.effectiveFrom ? asText(r.effectiveFrom) : null,
    updatedAt: r.updatedAt ? asText(r.updatedAt) : null,
  };
};

const mapRoleSummary = (raw: unknown): EmployeeRoleSummary | null => {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const roleUuid = asText(r.roleUuid);
  if (!roleUuid) return null;
  return {
    roleUuid,
    name: asText(r.name),
    status: asText(r.status),
  };
};

const mapEmployee = (raw: unknown): EmployeeRow => {
  const r = (raw || {}) as Record<string, unknown>;
  return {
    employeeUuid: asText(r.employeeUuid),
    name: asText(r.name),
    phone: asText(r.phone),
    email: asText(r.email),
    status: asText(r.status),
    showInApp: Boolean(r.showInApp),
    gender: asText(r.gender) || 'UNSPECIFIED',
    role: mapRoleSummary(r.role),
    createdAt: asText(r.createdAt),
    updatedAt: asText(r.updatedAt),
  };
};

const mapEmployeeDocument = (raw: unknown): EmployeeDocumentRow => {
  const r = (raw || {}) as Record<string, unknown>;
  const fileSizeValue = r.fileSizeBytes;
  return {
    documentUuid: asText(r.documentUuid),
    employeeUuid: asText(r.employeeUuid),
    documentType: asText(r.documentType),
    displayName: asText(r.displayName),
    fileName: asText(r.fileName),
    contentType: asText(r.contentType),
    fileSizeBytes: typeof fileSizeValue === 'number'
      ? fileSizeValue
      : Number(fileSizeValue || 0),
    documentUrl: asText(r.documentUrl),
    createdAt: asText(r.createdAt),
  };
};

const buildQuery = (params: Record<string, string | undefined>): string => {
  const usable = Object.entries(params).filter(([, v]) => v && v.trim().length > 0);
  if (usable.length === 0) return '';
  const search = new URLSearchParams();
  for (const [k, v] of usable) search.set(k, String(v));
  return `?${search.toString()}`;
};

const MODULE = 'employees-roles-client';

const REQUEST_NAMES = {
  listRoles: 'staffing-roles-list',
  createRole: 'staffing-role-create',
  updateRole: 'staffing-role-update',
  updateRoleStatus: 'staffing-role-status',
  deleteRole: 'staffing-role-delete',
  listEmployees: 'staffing-employees-list',
  getEmployee: 'staffing-employee-detail',
  createEmployee: 'staffing-employee-create',
  updateEmployee: 'staffing-employee-update',
  updateEmployeeStatus: 'staffing-employee-status',
  updateEmployeeRole: 'staffing-employee-role-update',
  deleteEmployee: 'staffing-employee-delete',
  listEmployeeDocuments: 'staffing-employee-documents-list',
  uploadEmployeeDocument: 'staffing-employee-document-upload',
  deleteEmployeeDocument: 'staffing-employee-document-delete',
  requestEmployeeDocumentAccessOtp: 'staffing-employee-document-access-request-otp',
  verifyEmployeeDocumentAccessOtp: 'staffing-employee-document-access-verify-otp',
  downloadEmployeeDocument: 'staffing-employee-document-access-download',
  getSchedule: 'staffing-schedule-detail',
  replaceWeeklySchedule: 'staffing-schedule-replace',
  upsertScheduleException: 'staffing-schedule-exception-upsert',
  deleteScheduleException: 'staffing-schedule-exception-delete',
  listEmployeeAssignments: 'staffing-employee-assignments-list',
  listServiceAssignments: 'staffing-service-assignments-list',
  assignServiceToEmployee: 'staffing-assignment-create-by-employee',
  assignEmployeeToService: 'staffing-assignment-create-by-service',
  removeEmployeeAssignment: 'staffing-assignment-delete-by-employee',
  removeServiceAssignment: 'staffing-assignment-delete-by-service',
  listActiveServices: 'staffing-service-picker-list',
} as const;

const logEvent = (event: string, fields: Record<string, unknown> = {}): void => {
  // Mirrors backend structured logging — every frontend API call leaves a
  // trace anchored by `event` and the relevant identifier so console-based
  // debugging can correlate with backend logs by `roleUuid`.
  logger.info(MODULE, event, { event, ...fields });
};

export const employeesApi = {
  async listRoles(statusFilter?: string): Promise<EmployeeRoleRow[]> {
    const path = statusFilter
      ? `${ROLES_PATH}?status=${encodeURIComponent(statusFilter)}`
      : ROLES_PATH;
    logEvent('employee_roles_list_request', { statusFilter });
    const payload = await request<unknown>(path, {
      method: 'GET',
      requestName: REQUEST_NAMES.listRoles,
    });
    const list = unwrap<unknown[]>(payload as ApiEnvelope<unknown[]>);
    const rows = Array.isArray(list) ? list.map(mapRole) : [];
    logEvent('employee_roles_list_response', { count: rows.length });
    return rows;
  },

  async createRole(input: EmployeeRoleWriteInput): Promise<EmployeeRoleRow> {
    logEvent('employee_role_create_request', { name: input.name });
    const payload = await request<unknown>(ROLES_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      requestName: REQUEST_NAMES.createRole,
      body: JSON.stringify({
        name: input.name,
        description: input.description ?? '',
      }),
    });
    const row = mapRole(unwrap<unknown>(payload as ApiEnvelope<unknown>));
    logEvent('employee_role_created', { roleUuid: row.roleUuid });
    return row;
  },

  async updateRole(roleUuid: string, input: EmployeeRoleWriteInput): Promise<EmployeeRoleRow> {
    logEvent('employee_role_update_request', { roleUuid, name: input.name });
    const payload = await request<unknown>(`${ROLES_PATH}/${encodeURIComponent(roleUuid)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      requestName: REQUEST_NAMES.updateRole,
      body: JSON.stringify({
        name: input.name,
        description: input.description ?? '',
      }),
    });
    const row = mapRole(unwrap<unknown>(payload as ApiEnvelope<unknown>));
    logEvent('employee_role_updated', { roleUuid: row.roleUuid });
    return row;
  },

  async updateRoleStatus(roleUuid: string, status: 'ACTIVE' | 'INACTIVE'): Promise<EmployeeRoleRow> {
    logEvent('employee_role_status_request', { roleUuid, status });
    const payload = await request<unknown>(`${ROLES_PATH}/${encodeURIComponent(roleUuid)}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      requestName: REQUEST_NAMES.updateRoleStatus,
      body: JSON.stringify({ status }),
    });
    const row = mapRole(unwrap<unknown>(payload as ApiEnvelope<unknown>));
    logEvent('employee_role_status_updated', { roleUuid: row.roleUuid, status: row.status });
    return row;
  },

  async deleteRole(roleUuid: string): Promise<void> {
    logEvent('employee_role_delete_request', { roleUuid });
    await request<unknown>(`${ROLES_PATH}/${encodeURIComponent(roleUuid)}`, {
      method: 'DELETE',
      requestName: REQUEST_NAMES.deleteRole,
    });
    logEvent('employee_role_deleted', { roleUuid });
  },

  // ---------- C5: Employee CRUD ----------

  async listEmployees(filters: EmployeeListFilters = {}): Promise<EmployeeRow[]> {
    const path = `${EMPLOYEES_PATH}${buildQuery({
      status: filters.status,
      roleUuid: filters.roleUuid,
    })}`;
    logEvent('employees_list_request', {
      statusFilter: filters.status,
      roleFilter: filters.roleUuid,
    });
    const payload = await request<unknown>(path, {
      method: 'GET',
      requestName: REQUEST_NAMES.listEmployees,
    });
    const list = unwrap<unknown[]>(payload as ApiEnvelope<unknown[]>);
    const rows = Array.isArray(list) ? list.map(mapEmployee) : [];
    logEvent('employees_list_response', {
      count: rows.length,
      statusFilter: filters.status,
      roleFilter: filters.roleUuid,
    });
    return rows;
  },

  async getEmployee(employeeUuid: string): Promise<EmployeeRow> {
    logEvent('employee_get_request', { employeeUuid });
    const payload = await request<unknown>(`${EMPLOYEES_PATH}/${encodeURIComponent(employeeUuid)}`, {
      method: 'GET',
      requestName: REQUEST_NAMES.getEmployee,
    });
    const row = mapEmployee(unwrap<unknown>(payload as ApiEnvelope<unknown>));
    logEvent('employee_get_response', { employeeUuid: row.employeeUuid });
    return row;
  },

  async createEmployee(input: EmployeeWriteInput): Promise<EmployeeRow> {
    logEvent('employee_create_request', { roleUuid: input.roleUuid });
    const payload = await request<unknown>(EMPLOYEES_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      requestName: REQUEST_NAMES.createEmployee,
      body: JSON.stringify({
        name: input.name,
        phone: input.phone ?? '',
        email: input.email ?? '',
        roleUuid: input.roleUuid,
        showInApp: Boolean(input.showInApp),
        gender: input.gender || 'UNSPECIFIED',
      }),
    });
    const row = mapEmployee(unwrap<unknown>(payload as ApiEnvelope<unknown>));
    logEvent('employee_created', { employeeUuid: row.employeeUuid });
    return row;
  },

  async updateEmployee(employeeUuid: string, input: EmployeeWriteInput): Promise<EmployeeRow> {
    logEvent('employee_update_request', { employeeUuid, roleUuid: input.roleUuid });
    const payload = await request<unknown>(`${EMPLOYEES_PATH}/${encodeURIComponent(employeeUuid)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      requestName: REQUEST_NAMES.updateEmployee,
      body: JSON.stringify({
        name: input.name,
        phone: input.phone ?? '',
        email: input.email ?? '',
        roleUuid: input.roleUuid,
        showInApp: Boolean(input.showInApp),
        gender: input.gender || 'UNSPECIFIED',
      }),
    });
    const row = mapEmployee(unwrap<unknown>(payload as ApiEnvelope<unknown>));
    logEvent('employee_updated', { employeeUuid: row.employeeUuid });
    return row;
  },

  async updateEmployeeStatus(
    employeeUuid: string,
    status: EmployeeStatusInput['status'],
  ): Promise<EmployeeRow> {
    logEvent('employee_status_request', { employeeUuid, status });
    const payload = await request<unknown>(
      `${EMPLOYEES_PATH}/${encodeURIComponent(employeeUuid)}/status`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        requestName: REQUEST_NAMES.updateEmployeeStatus,
        body: JSON.stringify({ status }),
      },
    );
    const row = mapEmployee(unwrap<unknown>(payload as ApiEnvelope<unknown>));
    logEvent('employee_status_updated', { employeeUuid: row.employeeUuid, status: row.status });
    return row;
  },

  async updateEmployeeRole(employeeUuid: string, roleUuid: string): Promise<EmployeeRow> {
    logEvent('employee_role_change_request', { employeeUuid, roleUuid });
    const payload = await request<unknown>(
      `${EMPLOYEES_PATH}/${encodeURIComponent(employeeUuid)}/role`,
      {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        requestName: REQUEST_NAMES.updateEmployeeRole,
        body: JSON.stringify({ roleUuid }),
      },
    );
    const row = mapEmployee(unwrap<unknown>(payload as ApiEnvelope<unknown>));
    logEvent('employee_role_changed', {
      employeeUuid: row.employeeUuid,
      roleUuid: row.role?.roleUuid || null,
    });
    return row;
  },

  async deleteEmployee(employeeUuid: string): Promise<void> {
    logEvent('employee_delete_request', { employeeUuid });
    await request<unknown>(`${EMPLOYEES_PATH}/${encodeURIComponent(employeeUuid)}`, {
      method: 'DELETE',
      requestName: REQUEST_NAMES.deleteEmployee,
    });
    logEvent('employee_deleted', { employeeUuid });
  },

  async listEmployeeDocuments(employeeUuid: string): Promise<EmployeeDocumentRow[]> {
    logEvent('employee_documents_list_request', { employeeUuid });
    const payload = await request<unknown>(employeeDocumentsPath(employeeUuid), {
      method: 'GET',
      requestName: REQUEST_NAMES.listEmployeeDocuments,
    });
    const list = unwrap<unknown[]>(payload as ApiEnvelope<unknown[]>);
    const rows = Array.isArray(list) ? list.map(mapEmployeeDocument) : [];
    logEvent('employee_documents_list_response', { employeeUuid, count: rows.length });
    return rows;
  },

  async uploadEmployeeDocument(
    employeeUuid: string,
    input: EmployeeDocumentUploadInput,
  ): Promise<EmployeeDocumentRow> {
    const formData = new FormData();
    formData.append('documentType', input.documentType);
    formData.append('file', input.file);
    if (input.displayName && input.displayName.trim()) {
      formData.append('displayName', input.displayName.trim());
    }

    logEvent('employee_document_upload_request', {
      employeeUuid,
      documentType: input.documentType,
      hasDisplayName: Boolean(input.displayName && input.displayName.trim()),
      fileName: input.file?.name || '',
    });
    const payload = await request<unknown>(employeeDocumentsPath(employeeUuid), {
      method: 'POST',
      requestName: REQUEST_NAMES.uploadEmployeeDocument,
      body: formData,
      isFileUpload: true,
    });
    const row = mapEmployeeDocument(unwrap<unknown>(payload as ApiEnvelope<unknown>));
    logEvent('employee_document_uploaded', {
      employeeUuid: row.employeeUuid,
      documentUuid: row.documentUuid,
      documentType: row.documentType,
    });
    return row;
  },

  async deleteEmployeeDocument(employeeUuid: string, documentUuid: string): Promise<void> {
    logEvent('employee_document_delete_request', { employeeUuid, documentUuid });
    await request<unknown>(
      `${employeeDocumentsPath(employeeUuid)}/${encodeURIComponent(documentUuid)}`,
      {
        method: 'DELETE',
        requestName: REQUEST_NAMES.deleteEmployeeDocument,
      },
    );
    logEvent('employee_document_deleted', { employeeUuid, documentUuid });
  },

  async requestEmployeeDocumentAccessOtp(
    employeeUuid: string,
    documentUuid: string,
  ): Promise<EmployeeDocumentAccessOtpIssue> {
    logEvent('employee_document_access_otp_request', { employeeUuid, documentUuid });
    const payload = await request<unknown>(
      `${employeeDocumentAccessPath(employeeUuid, documentUuid)}/request-otp`,
      {
        method: 'POST',
        requestName: REQUEST_NAMES.requestEmployeeDocumentAccessOtp,
      },
    );
    const data = unwrap<Record<string, unknown>>(payload as ApiEnvelope<Record<string, unknown>>);
    return {
      maskedEmail: asText(data.maskedEmail),
      otpTtlMinutes: Number(data.otpTtlMinutes || 0),
    };
  },

  async verifyEmployeeDocumentAccessOtp(
    employeeUuid: string,
    documentUuid: string,
    otp: string,
  ): Promise<EmployeeDocumentAccessGrant> {
    logEvent('employee_document_access_otp_verify_request', { employeeUuid, documentUuid });
    const payload = await request<unknown>(
      `${employeeDocumentAccessPath(employeeUuid, documentUuid)}/verify-otp`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        requestName: REQUEST_NAMES.verifyEmployeeDocumentAccessOtp,
        body: JSON.stringify({ otp }),
      },
    );
    const data = unwrap<Record<string, unknown>>(payload as ApiEnvelope<Record<string, unknown>>);
    return {
      accessToken: asText(data.accessToken),
      expiresInMinutes: Number(data.expiresInMinutes || 0),
    };
  },

  async downloadEmployeeDocument(
    employeeUuid: string,
    documentUuid: string,
    accessToken: string,
  ): Promise<Blob> {
    const path = `${employeeDocumentAccessPath(employeeUuid, documentUuid)}/content`;
    const requestPath = `${path}?__req=${encodeURIComponent(REQUEST_NAMES.downloadEmployeeDocument)}`;
    const requestUrl = `${resolveApiBaseForPath(env.apiBase || '', path) || (env.apiBase || '')}${requestPath}`;
    logEvent('employee_document_download_request', { employeeUuid, documentUuid });
    const response = await fetch(requestUrl, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'X-Document-Access-Token': accessToken,
      },
    });
    if (!response.ok) {
      let message = 'Could not download employee document.';
      try {
        const contentType = response.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const data = await response.json() as { message?: string };
          if (data?.message) {
            message = data.message;
          }
        } else {
          const text = await response.text();
          if (text) {
            message = text;
          }
        }
      } catch {
        // keep fallback message
      }
      throw new Error(message);
    }
    return response.blob();
  },

  // ---------- C6: Schedule ----------

  async getSchedule(employeeUuid: string): Promise<EmployeeScheduleDetail> {
    logEvent('schedule_get_request', { employeeUuid });
    const payload = await request<unknown>(schedulePath(employeeUuid), {
      method: 'GET',
      requestName: REQUEST_NAMES.getSchedule,
    });
    const detail = mapScheduleDetail(unwrap<unknown>(payload as ApiEnvelope<unknown>));
    logEvent('schedule_get_response', {
      employeeUuid,
      hasWeekly: Object.keys(detail.weeklySchedule).length > 0,
      exceptionCount: detail.exceptions.length,
    });
    return detail;
  },

  async replaceWeeklySchedule(
    employeeUuid: string,
    input: EmployeeScheduleWriteInput,
  ): Promise<EmployeeScheduleDetail> {
    logEvent('schedule_replace_request', {
      employeeUuid,
      dayCount: Object.keys(input.weeklySchedule || {}).length,
    });
    const payload = await request<unknown>(schedulePath(employeeUuid), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      requestName: REQUEST_NAMES.replaceWeeklySchedule,
      body: JSON.stringify({
        weeklySchedule: input.weeklySchedule,
        effectiveFrom: input.effectiveFrom ?? null,
      }),
    });
    const detail = mapScheduleDetail(unwrap<unknown>(payload as ApiEnvelope<unknown>));
    logEvent('schedule_replaced', {
      employeeUuid: detail.employeeUuid,
      scheduleUuid: detail.scheduleUuid,
    });
    return detail;
  },

  async upsertScheduleException(
    employeeUuid: string,
    input: EmployeeScheduleExceptionWriteInput,
  ): Promise<EmployeeScheduleDetail> {
    logEvent('schedule_exception_upsert_request', {
      employeeUuid,
      date: input.date,
      type: input.type,
      intervalCount: input.intervals?.length || 0,
    });
    const payload = await request<unknown>(`${schedulePath(employeeUuid)}/exceptions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      requestName: REQUEST_NAMES.upsertScheduleException,
      body: JSON.stringify({
        date: input.date,
        type: input.type,
        intervals: input.type === 'CUSTOM_HOURS' ? (input.intervals || []) : [],
      }),
    });
    const detail = mapScheduleDetail(unwrap<unknown>(payload as ApiEnvelope<unknown>));
    logEvent('schedule_exception_upserted', {
      employeeUuid: detail.employeeUuid,
      date: input.date,
      type: input.type,
    });
    return detail;
  },

  async deleteScheduleException(
    employeeUuid: string,
    date: string,
  ): Promise<EmployeeScheduleDetail> {
    logEvent('schedule_exception_delete_request', { employeeUuid, date });
    const payload = await request<unknown>(
      `${schedulePath(employeeUuid)}/exceptions/${encodeURIComponent(date)}`,
      { method: 'DELETE', requestName: REQUEST_NAMES.deleteScheduleException },
    );
    const detail = mapScheduleDetail(unwrap<unknown>(payload as ApiEnvelope<unknown>));
    logEvent('schedule_exception_deleted', {
      employeeUuid: detail.employeeUuid,
      date,
    });
    return detail;
  },

  // ---------- C7: Assignments ----------

  async listEmployeeAssignments(employeeUuid: string): Promise<AssignmentRow[]> {
    logEvent('employee_assignments_list_request', { employeeUuid });
    const payload = await request<unknown>(employeeAssignmentsPath(employeeUuid), {
      method: 'GET',
      requestName: REQUEST_NAMES.listEmployeeAssignments,
    });
    const list = unwrap<unknown[]>(payload as ApiEnvelope<unknown[]>);
    const rows = Array.isArray(list) ? list.map(mapAssignment) : [];
    logEvent('employee_assignments_list_response', { employeeUuid, count: rows.length });
    return rows;
  },

  async listServiceAssignments(serviceUuid: string): Promise<AssignmentRow[]> {
    logEvent('service_assignments_list_request', { serviceUuid });
    const payload = await request<unknown>(serviceAssignmentsPath(serviceUuid), {
      method: 'GET',
      requestName: REQUEST_NAMES.listServiceAssignments,
    });
    const list = unwrap<unknown[]>(payload as ApiEnvelope<unknown[]>);
    const rows = Array.isArray(list) ? list.map(mapAssignment) : [];
    logEvent('service_assignments_list_response', { serviceUuid, count: rows.length });
    return rows;
  },

  async assignServiceToEmployee(
    employeeUuid: string,
    serviceUuid: string,
    input: AssignmentWriteInput = {},
  ): Promise<AssignmentRow> {
    logEvent('assignment_by_employee_create_request', { employeeUuid, serviceUuid });
    const payload = await request<unknown>(employeeAssignmentsPath(employeeUuid), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      requestName: REQUEST_NAMES.assignServiceToEmployee,
      body: JSON.stringify({
        serviceUuid,
        priceOverride: input.priceOverride ?? null,
        isPrimary: input.isPrimary ?? false,
        visibility: input.visibility ?? 'PUBLIC',
      }),
    });
    const row = mapAssignment(unwrap<unknown>(payload as ApiEnvelope<unknown>));
    logEvent('assignment_by_employee_created', {
      employeeUuid: row.employeeUuid,
      serviceUuid: row.serviceUuid,
      assignmentUuid: row.assignmentUuid,
    });
    return row;
  },

  async assignEmployeeToService(
    serviceUuid: string,
    employeeUuid: string,
    input: AssignmentWriteInput = {},
  ): Promise<AssignmentRow> {
    logEvent('assignment_by_service_create_request', { serviceUuid, employeeUuid });
    const payload = await request<unknown>(serviceAssignmentsPath(serviceUuid), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      requestName: REQUEST_NAMES.assignEmployeeToService,
      body: JSON.stringify({
        employeeUuid,
        priceOverride: input.priceOverride ?? null,
        isPrimary: input.isPrimary ?? false,
        visibility: input.visibility ?? 'PUBLIC',
      }),
    });
    const row = mapAssignment(unwrap<unknown>(payload as ApiEnvelope<unknown>));
    logEvent('assignment_by_service_created', {
      employeeUuid: row.employeeUuid,
      serviceUuid: row.serviceUuid,
      assignmentUuid: row.assignmentUuid,
    });
    return row;
  },

  async removeEmployeeAssignment(employeeUuid: string, serviceUuid: string): Promise<void> {
    logEvent('assignment_remove_request', { employeeUuid, serviceUuid });
    await request<unknown>(
      `${employeeAssignmentsPath(employeeUuid)}/${encodeURIComponent(serviceUuid)}`,
      { method: 'DELETE', requestName: REQUEST_NAMES.removeEmployeeAssignment },
    );
    logEvent('assignment_removed', { employeeUuid, serviceUuid });
  },

  async removeServiceAssignment(serviceUuid: string, employeeUuid: string): Promise<void> {
    logEvent('assignment_remove_request', { employeeUuid, serviceUuid });
    await request<unknown>(
      `${serviceAssignmentsPath(serviceUuid)}/${encodeURIComponent(employeeUuid)}`,
      { method: 'DELETE', requestName: REQUEST_NAMES.removeServiceAssignment },
    );
    logEvent('assignment_removed', { employeeUuid, serviceUuid });
  },

  /**
   * Flat list of services for the assignment picker, sourced from the existing
   * catalog-by-category endpoint to avoid introducing a new backend route.
   * Only returns ACTIVE services.
   */
  async listActiveServices(): Promise<ServicePickerRow[]> {
    logEvent('catalog_services_list_request', {});
    const payload = await request<unknown>(CATALOG_SERVICES_BY_CATEGORY_PATH, {
      method: 'GET',
      requestName: REQUEST_NAMES.listActiveServices,
    });
    const groups = unwrap<unknown[]>(payload as ApiEnvelope<unknown[]>);
    const out: ServicePickerRow[] = [];
    if (Array.isArray(groups)) {
      for (const group of groups) {
        const g = (group || {}) as Record<string, unknown>;
        const services = Array.isArray(g.services) ? (g.services as unknown[]) : [];
        for (const svc of services) {
          const row = mapServicePickerRow(svc);
          if (row.serviceUuid && row.status === 'ACTIVE') out.push(row);
        }
      }
    }
    logEvent('catalog_services_list_response', { count: out.length });
    return out;
  },
};
